import { ReactElement, useMemo } from "react";
import { Center, List, Stack, Text } from "@mantine/core";
import { Dropzone, FileRejection, FileWithPath } from "@mantine/dropzone";
import { IconCloudUpload, IconUpload, IconX } from "@tabler/icons-react";
import { AcceptedFileType } from "./types";
import { getDropzoneDescription, getMimeTypesFromAcceptedFileType } from "./utils";
import {
    flexidaoGreen,
    flexidaoGrey,
    flexidaoRed,
    NotificationVariant,
    showNotification,
} from "@flexidao/ui-lib";

type FileDropZoneProps = {
    maxFiles?: number;
    totalMaxFiles?: number;
    maxSizeBytes?: number;
    acceptedFileTypes?: Array<AcceptedFileType>;
    onFilesDropped: (files: Array<FileWithPath>) => void;
    disabled?: boolean;
};

export const FileDropZone = ({
    maxFiles,
    totalMaxFiles = maxFiles,
    maxSizeBytes,
    acceptedFileTypes,
    onFilesDropped,
    disabled,
}: FileDropZoneProps): ReactElement => {
    const acceptedMimeTypes: Array<string> | undefined = useMemo(
        () => acceptedFileTypes?.flatMap(getMimeTypesFromAcceptedFileType) ?? undefined,
        [acceptedFileTypes],
    );

    const description: string = useMemo(
        () =>
            getDropzoneDescription({
                maxFiles: totalMaxFiles,
                maxSizeBytes,
            }),
        [totalMaxFiles, maxSizeBytes],
    );

    return (
        <Dropzone
            // Action props
            onDrop={onFilesDropped}
            onReject={(fileRejections: Array<FileRejection>): void => {
                fileRejections.forEach(({ file, errors }) => {
                    showNotification({
                        variant: NotificationVariant.Error,
                        title: `Error while processing file ${file.name}`,
                        message: (
                            <List>
                                {errors.map(({ message }) => (
                                    <List.Item key={message}>{message}</List.Item>
                                ))}
                            </List>
                        ),
                    });
                });
            }}
            disabled={disabled}
            // ----------------
            // Validation props
            multiple={maxFiles !== undefined && maxFiles > 1}
            maxFiles={maxFiles}
            maxSize={maxSizeBytes}
            accept={acceptedMimeTypes}
            // ----------------
            // UX props
            activateOnClick
            activateOnDrag
            activateOnKeyboard
            // ----------------
            // Style props
            sx={
                disabled
                    ? {
                          backgroundColor: flexidaoGrey[0],
                          cursor: "not-allowed",

                          "& *": {
                              color: `${flexidaoGrey[5]} !important`,
                          },
                      }
                    : undefined
            }
        >
            <Center
                style={{
                    minHeight: "250px",
                    pointerEvents: "none",
                }}
            >
                <Stack align="center" spacing="8px">
                    <Dropzone.Idle>
                        <IconCloudUpload size="48px" stroke={1} />
                    </Dropzone.Idle>

                    <Dropzone.Accept>
                        <IconUpload size="48px" stroke={1} color={flexidaoGreen[5]} />
                    </Dropzone.Accept>

                    <Dropzone.Reject>
                        <IconX size="48px" stroke={1} color={flexidaoRed[5]} />
                    </Dropzone.Reject>

                    <Stack spacing="16px">
                        <Stack spacing="2px">
                            <Text fz="16px" lh="16px" ta="center">
                                Drop your files here
                            </Text>

                            <Text fz="14px" lh="12px" ta="center">
                                or click to browse your computer
                            </Text>
                        </Stack>

                        <Text size="14px" color="dimmed" ta="center">
                            {description}
                        </Text>
                    </Stack>
                </Stack>
            </Center>
        </Dropzone>
    );
};
